import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Container } from '../../../../../../shared/services/api/c4-model/SistemasService';
import { ContainerService } from '../../../../../../shared/services/api/c4-model/ContainerService';

type TipoContainer = {
  id: number;
  nome: string;
};

type Row = {
  id: string;
  tipo: number;
  meioAcesso: string;
  isEditing?: boolean;
  isNew?: boolean;
};

type Props = {
  initialRows?: Container[];
  onAcessoContainersChange?: (acessos: any[]) => void;
};

const convertToRows = (containers: Container[]): Row[] => {
  return containers.flatMap((item) =>
    item.acessoContainers.map((acesso) => ({
      id: acesso.id,
      tipo: acesso.tipo,
      meioAcesso: acesso.meioAcesso,
    }))
  );
};

export const ListaAcessoContainers: React.FC<Props> = ({
  initialRows = [],
  onAcessoContainersChange,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<{
    tipo: number;
    meioAcesso: string;
  }>({
    tipo: 1, // FRONTEND
    meioAcesso: '',
  });
  const [tiposContainer, setTiposContainer] = useState<TipoContainer[]>([]);

  const getTipoContainerNome = (id: number): string => {
    const tipo = tiposContainer.find((t) => t.id === id);
    return tipo ? tipo.nome : `Tipo ${id}`;
  };

  useEffect(() => {
    // Carregar tipos de container
    ContainerService.getAll().then((data) => {
      if (Array.isArray(data)) {
        setTiposContainer(data);
      }
    });
  }, []);

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (row: Row) => {
    setEditingRowId(row.id);
    setEditingValues({
      tipo: row.tipo,
      meioAcesso: row.meioAcesso,
    });
  };

  const handleSave = (id: string) => {
    const newRows = rows.map((row) =>
      row.id === id
        ? {
          ...row,
          tipo: editingValues.tipo,
          meioAcesso: editingValues.meioAcesso,
          isEditing: false,
          isNew: false,
        }
        : row
    );
    setRows(newRows);
    setEditingRowId(null);
    setEditingValues({ tipo: 1, meioAcesso: '' }); // FRONTEND

    if (onAcessoContainersChange) {
      const filteredRows = newRows.filter((row) => !row.isNew);
      onAcessoContainersChange(filteredRows);
    }
  };

  const handleCancel = (id: string) => {
    const newRows = rows.filter((row) => !(row.id === id && row.isNew));
    setRows(newRows);
    setEditingRowId(null);
    setEditingValues({ tipo: 1, meioAcesso: '' }); // FRONTEND
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    if (onAcessoContainersChange) {
      onAcessoContainersChange(newRows);
    }
  };

  const handleAddNew = () => {
    const newRow: Row = {
      id: String(Date.now()),
      tipo: 1, // FRONTEND
      meioAcesso: '',
      isEditing: true,
      isNew: true,
    };
    setRows([newRow, ...rows]);
    setEditingRowId(newRow.id);
    setEditingValues({ tipo: 1, meioAcesso: '' }); // FRONTEND
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      <Button
        variant="contained"
        color="primary"
        sx={{ width: '80px' }}
        onClick={handleAddNew}
        startIcon={<AddIcon />}
      >
        Novo
      </Button>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Meio de acesso</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {editingRowId === row.id ? (
                    <>
                      <Tooltip title="Salvar">
                        <IconButton
                          size="small"
                          onClick={() => handleSave(row.id)}
                          disabled={!editingValues.meioAcesso.trim()}
                          color="primary"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          onClick={() => handleCancel(row.id)}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {editingRowId === row.id ? (
                    <FormControl size="small" fullWidth>
                      <Select
                        value={editingValues.tipo}
                        onChange={(e) =>
                          setEditingValues((prev) => ({
                            ...prev,
                            tipo: Number(e.target.value),
                          }))
                        }
                      >
                        {tiposContainer.map((tipo) => (
                          <MenuItem key={tipo.id} value={tipo.id}>
                            {tipo.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    getTipoContainerNome(row.tipo)
                  )}
                </TableCell>
                <TableCell>
                  {editingRowId === row.id ? (
                    <TextField
                      value={editingValues.meioAcesso}
                      onChange={(e) =>
                        setEditingValues((prev) => ({
                          ...prev,
                          meioAcesso: e.target.value,
                        }))
                      }
                      placeholder="Meio de acesso"
                      size="small"
                      fullWidth
                      autoFocus
                    />
                  ) : (
                    row.meioAcesso
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
